
import { useToast } from '@/hooks/use-toast';
import { Assessment } from '@/types/assessment';
import { useAssessmentValidation } from './utils';
import { saveAssessmentSettings, saveAssessmentContent, publishAssessmentToLive } from './api';
import { AssessmentEditState } from './useAssessmentState';

interface UseSaveOperationsProps {
  state: AssessmentEditState;
  setLoading: (loading: boolean) => void;
  setAssessment: (assessment: Assessment | null) => void;
  setConfirmingVersionChange: (confirming: boolean) => void;
  populateAssessmentData: (assessment: Assessment) => void;
  id?: string;
}

export function useSaveOperations({ 
  state, 
  setLoading, 
  setAssessment, 
  setConfirmingVersionChange,
  populateAssessmentData,
  id 
}: UseSaveOperationsProps) {
  const { toast } = useToast();
  const { validateAssessment } = useAssessmentValidation(state.title, state.slug, toast);

  // Save assessment settings only (no version change)
  const saveSettings = async (): Promise<boolean> => {
    if (!id || !validateAssessment()) return false;
    
    setLoading(true);
    try {
      const updatedAssessment = await saveAssessmentSettings(id, {
        title: state.title,
        description: state.description,
        slug: state.slug,
        isActive: state.isActive,
        notifyEmails: state.notifyEmails,
        calendlyLink: state.calendlyLink
      });
      
      if (updatedAssessment) {
        setAssessment(updatedAssessment);
        populateAssessmentData(updatedAssessment);
        
        toast({
          title: "Success",
          description: "Assessment settings updated successfully"
        });
        return true;
      }
      return false;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update assessment settings"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Save assessment content (will trigger version change if published)
  const saveContent = async (): Promise<boolean> => {
    if (!id || !validateAssessment()) return false;
    
    setLoading(true);
    try {
      const updatedAssessment = await saveAssessmentContent(id, {
        categories: state.categories,
      });
      
      if (updatedAssessment) {
        setAssessment(updatedAssessment);
        populateAssessmentData(updatedAssessment);
        
        toast({
          title: "Success",
          description: updatedAssessment.version > (state.assessment?.version || 0)
            ? `Assessment updated to version ${updatedAssessment.version}`
            : "Assessment content updated successfully"
        });
        return true;
      }
      return false;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update assessment content"
      });
      return false;
    } finally {
      setLoading(false);
      setConfirmingVersionChange(false);
    }
  };

  // Handle publishing a draft assessment
  const handlePublish = async (): Promise<boolean> => {
    if (!id) return false;
    
    if (!validateAssessment()) {
      return false;
    }
    
    setLoading(true);
    try {
      // First save any current changes
      await saveAssessmentSettings(id, {
        title: state.title,
        description: state.description,
        slug: state.slug,
        categories: state.categories,
        notifyEmails: state.notifyEmails,
        calendlyLink: state.calendlyLink
      });
      
      // Then publish the assessment
      const publishedAssessment = await publishAssessmentToLive(id);
      
      if (publishedAssessment) {
        setAssessment(publishedAssessment);
        populateAssessmentData(publishedAssessment);
        
        toast({
          title: "Success",
          description: "Assessment published successfully (version 1)"
        });
        return true;
      }
      return false;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to publish assessment"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    saveSettings,
    saveContent,
    handlePublish,
  };
}
